import React, { FC } from 'react'
import { useHistory } from 'react-router'
import { omit } from 'lodash'
import queryString from 'query-string'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import { Button } from 'transfix-ui/components/Button'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Box, makeStyles, Typography, Grid } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { LoadRecommendation } from 'apps/fleet-planner/types'
import ShipmentCard, { getRecommendedShipment } from '../../shipment/ShipmentCard'
import { IRecommendedShipmentFragment } from '../../shipment/ShipmentCard/graphql/RecommendedShipmentFragment'

const useStyles = makeStyles(theme => ({
  location: {
    fontWeight: theme.typography.fontWeightBold,
  },
  coolGray4Bg: {
    backgroundColor: brandColors.coolGray4,
  },
  cardsContainer: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
  },
}))

interface IBookableShipmentSearchRunItAgainRowProps {
  loadRecommendation: LoadRecommendation
}

export const BookableShipmentSearchRunItAgainRow: FC<IBookableShipmentSearchRunItAgainRowProps> = ({
  loadRecommendation,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const { recommendations: loadRecommendations = [] } = loadRecommendation || {}
  const recommendations: IRecommendedShipmentFragment[] = loadRecommendations.map(rec =>
    getRecommendedShipment(rec)
  )
  const {
    deliveryCity,
    deliveryStateCode,
    pickupCity,
    pickupStateCode,
    recommendationsCount = 0,
    recommendationsUnviewedCount = 0,
  } = loadRecommendation || {}
  const { isMobile } = useBreakpointContext()
  const classes = useStyles()
  const openBookLoadsSavedSearchHandler = () => {
    trackEvent('Historical Lanes', 'CLICKED_SEE_ALL_LOADS_ON_RUN_IT_AGAIN_PAGE')
    const filters = {
      ...omit(loadRecommendation, [
        'createdAt',
        'sourceType',
        'totalResultCount',
        'unviewedResultCount',
        'recommendations',
        'createdByParentId',
        'subscriberParentIds',
        'recommendationsCount',
        'recommendationsUnviewedCount',
      ]),
    }
    const query = queryString.stringify({
      ...filters,
    })
    history.push(`/book/?${query}`)
  }

  const isTagVisible = recommendationsCount > 0
  return (
    <Box p={isMobile ? 1 : 2}>
      {/* Desktop Header section */}
      {!isMobile && (
        <Box display='flex' alignItems='center' pl={2}>
          <Typography variant='body1'>Based on your load from&nbsp;</Typography>
          <Typography className={classes.location}>
            {`${pickupCity}, ${pickupStateCode}`}
          </Typography>
          <LaneArrowIcon size='large' className='mh2' />
          <Typography className={classes.location}>
            {`${deliveryCity}, ${deliveryStateCode} `}
          </Typography>

          {isTagVisible && (
            <Box pl={2}>
              <Tag
                label={`${recommendationsUnviewedCount} new`}
                size={TAG_SIZE.small}
                tagStyle={TAG_STYLE.info}
              />
            </Box>
          )}

          <Box display='flex' alignItems='center' ml='auto' pr={1}>
            <Button
              startIcon={<LaneArrowIcon />}
              color='tertiary'
              size='large'
              onClick={openBookLoadsSavedSearchHandler}>
              See All Loads
            </Button>
          </Box>
        </Box>
      )}
      {/* Mobile Header section */}
      {isMobile && (
        <Box pl={1} pt={2}>
          {isTagVisible && (
            <Tag
              label={`${recommendationsUnviewedCount} new`}
              size={TAG_SIZE.small}
              tagStyle={TAG_STYLE.info}
            />
          )}
          <Box pt={1}>
            <Typography>Based on your load from&nbsp;</Typography>
          </Box>
          <Box display='flex' alignItems='center'>
            <Typography className={classes.location}>
              {`${pickupCity}, ${pickupStateCode}`}
            </Typography>
            <LaneArrowIcon size='large' className='mh2' />
            <Typography className={classes.location}>
              {`${deliveryCity}, ${deliveryStateCode} `}
            </Typography>
          </Box>
        </Box>
      )}
      <Grid container spacing={3} className={classes.cardsContainer}>
        {recommendations.map(recommendation => {
          const { shipmentId: loadRecommendationId } = recommendation || {}
          return (
            <Grid key={loadRecommendationId} item md={4} xs={12}>
              <ShipmentCard
                dataTest={`recommendation-tile-${loadRecommendationId}`}
                recommendation={recommendation}
              />
            </Grid>
          )
        })}
      </Grid>
      {/* Mobile footer section */}
      {isMobile && (
        <Box p={1}>
          <Box display='flex' justifyContent='center' flexDirection='row'>
            <Button
              startIcon={<LaneArrowIcon />}
              color='tertiary'
              size='large'
              onClick={openBookLoadsSavedSearchHandler}>
              See All Loads
            </Button>
          </Box>
          <hr className={classes.coolGray4Bg} />
        </Box>
      )}
    </Box>
  )
}
